import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { cn } from '@/lib/utils';
import { loadFeatureFlags } from '@/components/utils/featureFlags';
import {
  LayoutDashboard,
  Users,
  Package,
  FileText,
  Settings,
  Building2,
  Heart,
  Upload,
  BarChart3,
  HelpCircle,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Shield,
  UserCog,
  Calculator,
  DollarSign,
  Calendar,
  TrendingUp,
  Target,
  RefreshCw,
  Database
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const adminNavItems = [
  { type: 'item', name: 'Dashboard', icon: LayoutDashboard, page: 'AdminDashboard' },
  
  { type: 'section', name: 'Management' },
  { type: 'item', name: 'Employers', icon: Building2, page: 'Employers' },
  { type: 'item', name: 'Employees', icon: Users, page: 'EmployeeManagement' },
  { type: 'item', name: 'Benefits', icon: Heart, page: 'BenefitsManagement' },
  { type: 'item', name: 'Categories', icon: Package, page: 'Categories' },
  
  { type: 'section', name: 'Enrollments' },
  { type: 'item', name: 'Periods', icon: Calendar, page: 'EnrollmentPeriods' },
  { type: 'item', name: 'Life Events', icon: Heart, page: 'QualifyingLifeEvents' },
  { type: 'item', name: 'All Enrollments', icon: FileText, page: 'EnrollmentReports' },
  { type: 'item', name: 'Claims', icon: DollarSign, page: 'ClaimsManagement' },
  
  { type: 'section', name: 'Reports & Analytics' },
  { type: 'item', name: 'Analytics', icon: BarChart3, page: 'Analytics' },
  { type: 'item', name: 'Payroll', icon: FileText, page: 'PayrollReports' },
  { type: 'item', name: 'Demographics', icon: Users, page: 'DemographicReports' },
  { type: 'item', name: 'Tax Savings', icon: TrendingUp, page: 'TaxSavingsReports' },
  { type: 'item', name: 'Penetration', icon: Target, page: 'PenetrationReports' },
  
  { type: 'section', name: 'Configuration' },
  { type: 'item', name: 'System Config', icon: Settings, page: 'SystemConfiguration' },
  { type: 'item', name: 'Rating Tables', icon: DollarSign, page: 'RatingTables' },
  { type: 'item', name: 'Tax Rates', icon: Calculator, page: 'TaxRateManagement' },
  { type: 'item', name: 'Email Templates', icon: FileText, page: 'EmailTemplates' },
  
  { type: 'section', name: 'Tools' },
  { type: 'item', name: 'Proposals', icon: FileText, page: 'Proposals' },
  { type: 'item', name: 'Upload Census', icon: Upload, page: 'CensusUpload' },
  { type: 'item', name: 'Company Admins', icon: UserCog, page: 'SubAdminManagement' },
  { type: 'item', name: 'Migrations', icon: Database, page: 'MigrationsManagement' },
  { type: 'item', name: 'Audit Log', icon: Shield, page: 'AuditLog' },
  { type: 'item', name: 'Updates', icon: RefreshCw, page: 'AdminUpdates' },
];

const companyAdminNavItems = [
  { type: 'item', name: 'Dashboard', icon: LayoutDashboard, page: 'EmployerDashboard' },
  
  { type: 'section', name: 'Management' },
  { type: 'item', name: 'Company Settings', icon: Building2, page: 'CompanySettings' },
  { type: 'item', name: 'Employees', icon: Users, page: 'EmployerEmployees' },
  { type: 'item', name: 'Benefits', icon: Heart, page: 'BenefitsManagement' },
  { type: 'item', name: 'Categories', icon: Package, page: 'Categories' },
  
  { type: 'section', name: 'Enrollments' },
  { type: 'item', name: 'Periods', icon: Calendar, page: 'EnrollmentPeriods' },
  { type: 'item', name: 'Life Events', icon: Heart, page: 'QualifyingLifeEvents' },
  { type: 'item', name: 'All Enrollments', icon: FileText, page: 'EmployerEnrollments' },
  { type: 'item', name: 'Claims', icon: DollarSign, page: 'ClaimsManagement' },
  
  { type: 'section', name: 'Reports' },
  { type: 'item', name: 'Payroll', icon: FileText, page: 'PayrollReports' },
  { type: 'item', name: 'Demographics', icon: Users, page: 'DemographicReports' },
  { type: 'item', name: 'Tax Savings', icon: TrendingUp, page: 'TaxSavingsReports' },
  { type: 'item', name: 'Penetration', icon: Target, page: 'PenetrationReports' },
  
  { type: 'section', name: 'Tools' },
  { type: 'item', name: 'Upload Census', icon: Upload, page: 'CensusUpload' },
  { type: 'item', name: 'Export Census', icon: Upload, page: 'ExportCensus' },
  { type: 'item', name: 'HR Managers', icon: UserCog, page: 'HRManagers' },
];

const employerNavItems = [
  { type: 'item', name: 'Dashboard', icon: LayoutDashboard, page: 'EmployerDashboard' },
  
  { type: 'section', name: 'Management' },
  { type: 'item', name: 'Employees', icon: Users, page: 'EmployerEmployees' },
  { type: 'item', name: 'Benefits', icon: Heart, page: 'BenefitsManagement' },
  { type: 'item', name: 'Categories', icon: Package, page: 'Categories' },

  { type: 'section', name: 'Enrollments' },
  { type: 'item', name: 'Life Events', icon: Heart, page: 'QualifyingLifeEvents' },
  { type: 'item', name: 'Enrollments', icon: FileText, page: 'EmployerEnrollments' },
  { type: 'item', name: 'Claims', icon: DollarSign, page: 'ClaimsManagement' },

  { type: 'section', name: 'Reports' },
  { type: 'item', name: 'Payroll', icon: FileText, page: 'PayrollReports' },
  { type: 'item', name: 'Demographics', icon: Users, page: 'DemographicReports' },
  { type: 'item', name: 'Tax Savings', icon: TrendingUp, page: 'TaxSavingsReports' },
  { type: 'item', name: 'Penetration', icon: Target, page: 'PenetrationReports' },

  { type: 'section', name: 'Tools' },
  { type: 'item', name: 'Upload Census', icon: Upload, page: 'CensusUpload' },
  { type: 'item', name: 'Export Census', icon: Upload, page: 'ExportCensus' },
];

const employeeNavItems = [
  { type: 'item', name: 'Dashboard', icon: LayoutDashboard, page: 'EmployeeDashboard' },
  
  { type: 'section', name: 'Benefits' },
  { type: 'item', name: 'My Benefits', icon: Heart, page: 'MyBenefits' },
  { type: 'item', name: 'Browse Benefits', icon: Package, page: 'BenefitsCatalog' },
  { type: 'item', name: 'Claims', icon: DollarSign, page: 'Claims' },
  
  { type: 'section', name: 'Planning' },
  { type: 'item', name: 'Premium Calculator', icon: Calculator, page: 'FlexOptimization' },
  { type: 'item', name: 'Family Members', icon: Users, page: 'FamilyMembers' },

  { type: 'section', name: 'Account' },
  { type: 'item', name: 'My Profile', icon: UserCog, page: 'EmployeeProfile' },
  { type: 'item', name: 'Security', icon: Shield, page: 'TwoFactorAuth' },
  { type: 'item', name: 'Help', icon: HelpCircle, page: 'Help' },
];

export default function Sidebar({ userRole, currentPage, collapsed, onToggle, onLogout }) {
  const [featureFlags, setFeatureFlags] = useState({});

  useEffect(() => {
    loadFlags();
  }, []);

  const loadFlags = async () => {
    const flags = await loadFeatureFlags();
    setFeatureFlags(flags);
  };
  
  // Get base navigation items for role
  let baseNavItems = userRole === 'admin' 
    ? adminNavItems 
    : userRole === 'sub_admin'
      ? companyAdminNavItems
      : userRole === 'employer_hr' 
        ? employerNavItems 
        : employeeNavItems;
  
  // Filter navigation items based on feature flags
  const navItems = baseNavItems.filter(item => {
    // Always show section headers and non-feature items
    if (item.type === 'section') return true;
    
    // Filter Claims based on claims_enabled flag
    if (item.page === 'Claims' || item.page === 'ClaimsManagement') {
      return featureFlags.claims_enabled !== false;
    }
    
    // Filter QLE based on qle_enabled flag
    if (item.page === 'QualifyingLifeEvents') {
      return featureFlags.qle_enabled !== false;
    }
    
    // Show all other items
    return true;
  });

  return (
    <TooltipProvider delayDuration={0}>
      <div className={cn(
        "h-screen bg-sidebar text-sidebar-foreground flex flex-col transition-all duration-300 fixed left-0 top-0 z-40",
        collapsed ? "w-20" : "w-64"
      )}>
        {/* Logo */}
        <div className={cn(
          "h-16 flex items-center border-b border-sidebar-border px-4",
          collapsed ? "justify-center" : "justify-between"
        )}>
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-lg tracking-tight">Marcon Benefits</span>
            </div>
          )}
          {collapsed && (
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
              <Heart className="w-4 h-4 text-white" />
            </div>
          )}
        </div>

        {/* Toggle Button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggle}
          className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-sidebar-accent border border-sidebar-border hover:bg-sidebar-accent/80 z-50"
        >
          {collapsed ? (
            <ChevronRight className="w-3 h-3" />
          ) : (
            <ChevronLeft className="w-3 h-3" />
          )}
        </Button>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {navItems.map((item, idx) => {
              // Section Header
              if (item.type === 'section') {
                if (collapsed) return null;
                return (
                  <div key={`section-${idx}`} className="pt-4 pb-2 px-3">
                    <p className="text-xs font-semibold text-sidebar-foreground/60 uppercase tracking-wider">
                      {item.name}
                    </p>
                  </div>
                );
              }
              
              // Navigation Item
              const isActive = currentPage === item.page;
              const Icon = item.icon;
              
              const linkContent = (
                <Link
                  to={createPageUrl(item.page)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
                    isActive 
                      ? "bg-sidebar-accent text-sidebar-accent-foreground border border-sidebar-ring" 
                      : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
                  )}
                >
                  <Icon className={cn("w-5 h-5 flex-shrink-0", isActive && "text-sidebar-accent-foreground")} />
                  {!collapsed && <span className="font-medium">{item.name}</span>}
                </Link>
              );

              if (collapsed) {
                return (
                  <Tooltip key={item.page}>
                    <TooltipTrigger asChild>
                      {linkContent}
                    </TooltipTrigger>
                    <TooltipContent side="right" className="bg-sidebar text-sidebar-foreground border-sidebar-border">
                      {item.name}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return <div key={item.page}>{linkContent}</div>;
            })}
          </nav>
        </ScrollArea>

        {/* Logout */}
        <div className="p-4 border-t border-sidebar-border">
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  onClick={onLogout}
                  className="w-full justify-center px-0 text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-red-500/20"
                >
                  <LogOut className="w-5 h-5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-sidebar text-sidebar-foreground border-sidebar-border">
                Logout
              </TooltipContent>
            </Tooltip>
          ) : (
            <Button
              variant="ghost"
              onClick={onLogout}
              className="w-full justify-start text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-red-500/20"
            >
              <LogOut className="w-5 h-5" />
              <span className="ml-3">Logout</span>
            </Button>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}